import { Injectable } from '@angular/core';
import { CanvasInputMode } from './canvas-input-mode.service';
import { CanvasService } from './canvas.service';
import { RectangleCorners } from '@components/editor/corner-radius-options/corner-radius-options.component';

@Injectable({ providedIn: 'root' })
export class RectangleService {
    constructor(private canvasService: CanvasService) {}

    getRectangleCorners(element): {
        [RectangleCorners.TOP_RIGHT_CORNER]: string;
        [RectangleCorners.TOP_LEFT_CORNER]: string;
        [RectangleCorners.BOTTOM_RIGHT_CORNER]: string;
        [RectangleCorners.BOTTOM_LEFT_CORNER]: string;
    } {
        const pathData = element.getAttribute('d');
        if (!pathData) return;

        const arcRegex = /A\s*([\d\.]+),\s*([\d\.]+)\s*[\d\s]*[\d\s]*[\d\s]*[\d\s]*([\d\.]+),\s*([\d\.]+)/g;

        const corners = {
            [RectangleCorners.TOP_RIGHT_CORNER]: '0',
            [RectangleCorners.TOP_LEFT_CORNER]: '0',
            [RectangleCorners.BOTTOM_RIGHT_CORNER]: '0',
            [RectangleCorners.BOTTOM_LEFT_CORNER]: '0',
        };

        let match;
        let cornerIndex = 0;

        // Parse each matching arc in the pathData
        while ((match = arcRegex.exec(pathData)) !== null) {
            let rx = match[1];

            switch (cornerIndex) {
                case 0:
                    corners[RectangleCorners.TOP_LEFT_CORNER] = rx;
                    break;
                case 1:
                    corners[RectangleCorners.BOTTOM_LEFT_CORNER] = rx;
                    break;
                case 2:
                    corners[RectangleCorners.BOTTOM_RIGHT_CORNER] = rx;
                    break;
                case 3:
                    corners[RectangleCorners.TOP_RIGHT_CORNER] = rx;
                    break;
            }

            cornerIndex++;
        }

        return corners;
    }

    redrawRectangleAsPath(
        element,
        corners: {
            [RectangleCorners.TOP_RIGHT_CORNER]: string;
            [RectangleCorners.TOP_LEFT_CORNER]: string;
            [RectangleCorners.BOTTOM_RIGHT_CORNER]: string;
            [RectangleCorners.BOTTOM_LEFT_CORNER]: string;
        }
    ) {
        const width = Number(element.getAttribute('width'));
        const height = Number(element.getAttribute('height'));
        const x = Number(element.getAttribute('x'));
        const y = Number(element.getAttribute('y'));
        const path = this.getpath(x, y, width, height, corners);

        const newPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        newPathElement.setAttribute('id', element.getAttribute('id'));
        newPathElement.setAttribute('x', element.getAttribute('x'));
        newPathElement.setAttribute('y', element.getAttribute('y'));
        newPathElement.setAttribute('type', CanvasInputMode.RECTANGLE);
        newPathElement.setAttribute('stroke-width', element.getAttribute('stroke-width'));
        newPathElement.setAttribute('stroke', element.getAttribute('stroke'));
        newPathElement.setAttribute('fill', element.getAttribute('fill'));
        newPathElement.setAttribute('opacity', element.getAttribute('opacity'));
        newPathElement.setAttribute('width', element.getAttribute('width'));
        newPathElement.setAttribute('height', element.getAttribute('height'));
        newPathElement.setAttribute('d', path);

        const transform = element.getAttribute('transform');
        if (transform) newPathElement.setAttribute('transform', transform);
        const strokeJoin = element.getAttribute('stroke-linejoin');
        if (transform) newPathElement.setAttribute('stroke-linejoin', strokeJoin);

        const el = document.getElementById(element.getAttribute('id'));
        el.remove();

        const svgRoot: any = this.canvasService.getSvgRoot();
        const layer = svgRoot.querySelector('.layer');
        layer.appendChild(newPathElement);

        return newPathElement;
    }

    redrawPathAsRectangle(element, radius) {
        const newRectElement = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        newRectElement.setAttribute('id', element.getAttribute('id'));
        newRectElement.setAttribute('x', element.getAttribute('x'));
        newRectElement.setAttribute('y', element.getAttribute('y'));
        newRectElement.setAttribute('stroke-width', element.getAttribute('stroke-width'));
        newRectElement.setAttribute('stroke', element.getAttribute('stroke'));
        newRectElement.setAttribute('fill', element.getAttribute('fill'));
        newRectElement.setAttribute('opacity', element.getAttribute('opacity'));
        newRectElement.setAttribute('width', element.getAttribute('width'));
        newRectElement.setAttribute('height', element.getAttribute('height'));
        newRectElement.setAttribute('rx', radius);

        const transform = element.getAttribute('transform');
        if (transform) newRectElement.setAttribute('transform', transform);
        const strokeJoin = element.getAttribute('stroke-linejoin');
        if (transform) newRectElement.setAttribute('stroke-linejoin', strokeJoin);

        const el = document.getElementById(element.getAttribute('id'));
        el.remove();

        const svgRoot: any = this.canvasService.getSvgRoot();
        const layer = svgRoot.querySelector('.layer');
        layer.appendChild(newRectElement);

        return newRectElement;
    }

    redrawPath(
        element,
        corners: {
            [RectangleCorners.TOP_RIGHT_CORNER]: string;
            [RectangleCorners.TOP_LEFT_CORNER]: string;
            [RectangleCorners.BOTTOM_RIGHT_CORNER]: string;
            [RectangleCorners.BOTTOM_LEFT_CORNER]: string;
        }
    ) {
        const width = Number(element.getAttribute('width'));
        const height = Number(element.getAttribute('height'));
        const x = Number(element.getAttribute('x'));
        const y = Number(element.getAttribute('y'));
        const path = this.getpath(x, y, width, height, corners);

        element.setAttribute('d', path);

        return element;
    }

    private getpath(
        x,
        y,
        width,
        height,
        corners: {
            [RectangleCorners.TOP_RIGHT_CORNER]: string;
            [RectangleCorners.TOP_LEFT_CORNER]: string;
            [RectangleCorners.BOTTOM_RIGHT_CORNER]: string;
            [RectangleCorners.BOTTOM_LEFT_CORNER]: string;
        }
    ) {
        const topRightRadius = Number(corners[RectangleCorners.TOP_RIGHT_CORNER]);
        const topLeftRadius = Number(corners[RectangleCorners.TOP_LEFT_CORNER]);
        const bottomRightRadius = Number(corners[RectangleCorners.BOTTOM_LEFT_CORNER]);
        const bottomLeftRadius = Number(corners[RectangleCorners.BOTTOM_RIGHT_CORNER]);

        const path = [
            `M${x + topRightRadius},${y}`,
            `H${x + width - topLeftRadius}`,
            `A${topLeftRadius},${topLeftRadius} 0 0 1 ${x + width},${y + topLeftRadius}`,
            `V${y + height - bottomRightRadius}`,
            `A${bottomRightRadius},${bottomRightRadius} 0 0 1 ${x + width - bottomRightRadius},${y + height}`,
            `H${x + bottomLeftRadius}`,
            `A${bottomLeftRadius},${bottomLeftRadius} 0 0 1 ${x},${y + height - bottomLeftRadius}`,
            `V${y + topRightRadius}`,
            `A${topRightRadius},${topRightRadius} 0 0 1 ${x + topRightRadius},${y}`,
            'Z',
        ].join(' ');

        return path;
    }
}
